/**
 * Picking commands back up after a reload (IR-126 §7.2.2, phase 3.2c).
 *
 * ## The failure this closes, and only this one
 *
 * You edit a task. The tab reloads while the command is still in flight. The
 * command later **fails**.
 *
 * Nothing is listening, so `reportOutcome` never runs and §4.5's dispatch never
 * fires. The task snapshot is authoritative after a reload, so the value the
 * user typed simply is not there. They watch their edit vanish with no message
 * — §7.2's stated worst case, arriving through the one door the earlier cards
 * left open. 3.2d bounded the wait, 3.2b stopped the cascade, 3.3 gave every
 * class a message; none of them survive a page load.
 *
 * A command that **applies** during a reload needs none of this: the snapshot
 * carries the new value and the user sees it. The gap is failures.
 *
 * ## What is already true, and is not re-implemented here
 *
 * §7.2.2's second sentence — the locally-advanced `base_seq` must not survive a
 * reload — already holds, because the ledger in `useTaskSeq` is a module-level
 * `Map` with no persistence. 3.2a chose in-memory for exactly this. Nothing in
 * this module writes to it; `rememberCommand` stores a command id, which is a
 * pointer to a server fact rather than a local guess.
 *
 * ## What a recovered command is, and is not
 *
 * It reaches `reportOutcome` like any other, because a failure is a failure
 * whenever it is observed. Two things are deliberately different:
 *
 * - **No rollback.** The reload already took the optimistic value; the task
 *   snapshot is what is on screen. There is nothing to undo, only something to
 *   say.
 * - **No chain.** `useTaskSeq`'s chain lives in the same lost `Map`, so a
 *   recovered failure cannot be `discarded` and cannot void anything. It is
 *   reported on its own, which is right: the commands it might have voided did
 *   not survive either.
 *
 * A recovered command reads as `saving` while it is being waited on — it
 * genuinely is in flight. If it then times out it reads as `unconfirmed`, the
 * same as any other timeout: we stopped watching, which is not the same as
 * knowing it failed.
 *
 * - **A conflict becomes a message.** §4.5 hands `stale` to the caller to show
 *   both values, and recovery cannot be that caller — the pointer carries
 *   `baseSeq` and nothing else, because the reload took the field and the
 *   attempted value with it. Left as `reportOutcome` leaves it, a recovered
 *   `stale` would produce no toast, no dialog and no message at all: the user's
 *   edit missing from the board with nothing to explain it. So it gets its own
 *   line instead — worse UI than the dialog, and the only alternative is
 *   silence, which §7.2 rules out.
 */
import { logger } from '@/utils/logger'
import { commandRef, watchCommand } from './useTaskCommands'
import type { Firestore } from 'firebase/firestore'
import { forgetCommand, pendingCommands } from './taskCommandPointers'
import type { CommandPointer } from './taskCommandPointers'
import { rebaseTask } from './useTaskSeq'
import { reportOutcome } from './useTaskCommandErrors'
import { useAppStore } from '@/stores/app'
import i18n from '@/plugins/i18n'
import { markPending, releasePending } from './useTaskPending'

export interface RecoverOptions {
  firestore?: Firestore
  /** Shorter than a fresh submission's — see {@link recoverPendingCommands}. */
  timeoutMs?: number
}

/**
 * How long to wait on a command that was already in flight before the reload.
 *
 * Deliberately shorter than a new command's 15s. That budget covers the whole
 * pipeline from write to apply; a recovered command has already spent some
 * unknown part of it, and the page load itself took time. Waiting the full
 * window again would leave a card showing `saving` for fifteen seconds over a
 * command that was probably decided before the tab came back.
 */
const RECOVERY_TIMEOUT_MS = 8_000

/**
 * Commands this tab is already waiting on.
 *
 * `recoverPendingCommands` runs on every board load, and a pointer is only
 * forgotten once its command settles — so leaving a board and re-entering
 * inside the recovery window would find the same pointer still there and attach
 * a **second** listener to it. Both would resolve, and the user would get two
 * toasts for one failure: the duplicate-report problem §5.1 spends 3.2b
 * preventing, arriving by a different route.
 *
 * Keyed by command id rather than project, because the second load can be a
 * different board — pointers for other projects stay in storage and are
 * recovered when that board opens, so two boards in quick succession can each
 * reach for commands the other is already holding.
 */
const recovering = new Set<string>()

async function recoverOne(pointer: CommandPointer, options: RecoverOptions): Promise<void> {
  const { projectId, taskId, commandId, baseSeq } = pointer
  let unconfirmedAt: number | undefined

  // Already being waited on by an earlier load. Returning rather than
  // re-attaching is what keeps one failure to one message.
  if (recovering.has(commandId)) return
  recovering.add(commandId)

  // The prediction did not survive the reload, and must not be reconstructed
  // from the pointer — §7.2.2. This makes that explicit rather than relying on
  // the Map having started empty, so a future change that warms the ledger on
  // load still lands on the server value for a task with a recovered command.
  rebaseTask(projectId, taskId)
  markPending(projectId, taskId)

  try {
    const outcome = await watchCommand(commandRef(projectId, commandId, options.firestore), {
      timeoutMs: options.timeoutMs ?? RECOVERY_TIMEOUT_MS,
      label: 'recovered',
      // No onRollback, deliberately: the reload took the optimistic value with
      // it and the snapshot is authoritative. There is nothing to undo.
    })

    // The bare outcome, no re-shaping. `reportOutcome` takes only what it reads
    // (status, errorCode, error, commandId) since 4.1a — it never needed
    // `baseSeq`, and a recovered `task_create` does not have one to give it.
    //
    // Never `discarded` either way: the chain that would have voided this did
    // not survive the reload.
    const verdict = reportOutcome(outcome)

    if (verdict.action === 'conflict') {
      // §4.5 hands a `stale` to the caller so it can show both values — and
      // recovery cannot be that caller. The pointer carries `baseSeq` and
      // nothing else: no field, no attempted value, because the reload took
      // them. There is no honest way to fill the conflict dialog.
      //
      // So it becomes a message. `reportOutcome` deliberately toasts nothing
      // for a conflict, which for every live caller is right and here would
      // mean the user's edit is missing from the board with no explanation of
      // any kind — the exact failure this card exists to close, surviving for
      // one error class. §7.2's rule is that silence is the unacceptable
      // outcome; "we cannot show the good UI" is not a reason to show none.
      useAppStore().toast(
        i18n.global.t('common.error'),
        i18n.global.t('tasks.errors.command.stale_after_reload'),
        'error',
      )
    }

    // `-1` when the pointer has no `baseSeq` — that is a recovered `task_create`
    // (§4.3), and its genesis revision is `seq 0`, so `-1` clears the notice the
    // moment the task exists. Anchoring at the absent value instead would leave
    // the warning up forever.
    unconfirmedAt = outcome.status === 'unresolved' ? (baseSeq ?? -1) : undefined
  } catch (e) {
    // The listener failed. The command is still queued and may well apply, so
    // this is not reported to the user as a lost edit — same reasoning as a
    // live listener death (§7.2.1).
    logger.error(`task command ${commandId}: recovery listener failed`, e)
  } finally {
    // Whatever happened, this tab is no longer waiting on it. Leaving the
    // pointer would re-attach again on the next load, forever.
    forgetCommand(commandId)
    releasePending(projectId, taskId, commandId, unconfirmedAt)
    recovering.delete(commandId)
  }
}

/**
 * Re-attach to every command this tab had in flight when it was last loaded.
 *
 * Safe to call more than once — a command already being waited on is skipped
 * rather than attached to twice — and safe to call with nothing pending. Never throws: recovery is a nicety, and a board that fails to load
 * because a pointer was malformed would be a worse bug than the one this fixes.
 *
 * Returns when every recovered command has settled, so a caller can await it in
 * a test. Production callers do not need to.
 */
export async function recoverPendingCommands(
  projectId: string,
  options: RecoverOptions = {},
): Promise<void> {
  const pointers = pendingCommands(projectId)
  if (pointers.length === 0) return

  logger.info(`task commands: re-attaching to ${pointers.length} after reload`)
  await Promise.all(pointers.map((p) => recoverOne(p, options)))
}

/** Test seam. Never call from application code. */
export function _resetRecovery(): void {
  recovering.clear()
}
